import {
  ComponentProps,
  KeyboardEvent,
  PropsWithChildren,
  createContext,
  useContext,
  useId,
  useState,
} from 'react';
import { MenuItem, makeStyles } from '@material-ui/core';
import { alpha } from '@mui/material/styles';
import { SidebarItem, useSidebarPinState } from '@backstage/core-components';
import { Link, useLocation, useResolvedPath } from 'react-router-dom';
import {
  getSidebarFlyoutPalette,
  SidebarHoverFlyoutPanel,
  useHoverFlyout,
  useSidebarHoverFlyoutStyles,
} from './SidebarHoverFlyout';
import { SIDEBAR_ITEM_LABEL_FONT_SIZE } from '../../theme/make';

export type SidebarSubmenuItem = {
  title: string;
  to: string;
};

export type SidebarSubmenuColumn = {
  ariaLabel: string;
  items: readonly SidebarSubmenuItem[];
};

type FlyoutCoordinator = {
  activeId?: string;
  open: (id: string) => void;
  close: (id: string) => void;
};

const SidebarFlyoutContext = createContext<FlyoutCoordinator | undefined>(
  undefined,
);

export const useSidebarFlyoutCoordinator = () =>
  useContext(SidebarFlyoutContext);

export const SidebarFlyoutProvider = ({ children }: PropsWithChildren<{}>) => {
  const [activeId, setActiveId] = useState<string>();

  return (
    <SidebarFlyoutContext.Provider
      value={{
        activeId,
        open: setActiveId,
        close: id =>
          setActiveId(current => (current === id ? undefined : current)),
      }}
    >
      {children}
    </SidebarFlyoutContext.Provider>
  );
};

const useStyles = makeStyles(theme => {
  const { color, continuation, hover } = getSidebarFlyoutPalette(theme);

  return {
    paper: {
      minWidth: 240,
      maxHeight: 'calc(100vh - 24px)',
      overflow: 'auto',
    },
    menu: {
      width: 240,
      boxSizing: 'border-box' as const,
      padding: theme.spacing(0.5),
    },
    columns: {
      display: 'flex',
      minWidth: 440,
      padding: theme.spacing(0.5),
    },
    column: {
      width: 220,
      minWidth: 0,
      '& + &': {
        borderLeft: `1px solid ${alpha(color, 0.12)}`,
      },
    },
    singleColumn: {
      width: '100%',
    },
    item: {
      '&&': {
        height: 40,
        minHeight: 40,
        color,
        borderLeft: '3px solid transparent',
        borderRadius: 8,
        fontSize: SIDEBAR_ITEM_LABEL_FONT_SIZE,
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: theme.typography.subtitle2.lineHeight,
      },
      '&&:hover': {
        backgroundColor: hover,
      },
      "&&[aria-current='page']": {
        color: `${continuation} !important`,
        backgroundColor: 'transparent',
        borderRadius: 0,
        borderLeft: `3px solid ${continuation}`,
      },
    },
  };
});

const pathMatches = (pathname: string, destination: string) =>
  pathname === destination || pathname.startsWith(`${destination}/`);

export const destinationMatchesLocation = (
  location: { pathname: string; search: string },
  destination: { pathname: string; search: string },
) => {
  if (!pathMatches(location.pathname, destination.pathname)) {
    return false;
  }

  const destinationSearch = new URLSearchParams(destination.search);
  if (destinationSearch.size === 0) {
    return true;
  }

  const currentSearch = new URLSearchParams(location.search);
  return [...destinationSearch.keys()].every(key => {
    const expectedValues = destinationSearch.getAll(key).sort();
    const currentValues = currentSearch.getAll(key).sort();
    return (
      expectedValues.length === currentValues.length &&
      expectedValues.every((value, index) => value === currentValues[index])
    );
  });
};

const SidebarFlyoutLink = ({ item }: { item: SidebarSubmenuItem }) => {
  const classes = useStyles();
  const location = useLocation();
  const resolved = useResolvedPath(item.to);
  const active = destinationMatchesLocation(location, resolved);

  return (
    <MenuItem
      component={Link}
      to={item.to}
      role="menuitem"
      tabIndex={-1}
      className={classes.item}
      aria-current={active ? 'page' : undefined}
    >
      {item.title}
    </MenuItem>
  );
};

type SidebarItemWithSubmenuProps = ComponentProps<typeof SidebarItem> & {
  text: string;
  to: string;
  items?: readonly SidebarSubmenuItem[];
  columns?: readonly SidebarSubmenuColumn[];
};

export const SidebarItemWithSubmenu = ({
  items = [],
  columns,
  text,
  to,
  ...sidebarItemProps
}: SidebarItemWithSubmenuProps) => {
  const classes = useStyles();
  const sharedClasses = useSidebarHoverFlyoutStyles();
  const coordinator = useSidebarFlyoutCoordinator();
  const generatedId = useId();
  const itemId = `${to}:${generatedId}`;
  const [localOpen, setLocalOpen] = useState(false);
  const { isMobile } = useSidebarPinState();
  const open = coordinator ? coordinator.activeId === itemId : localOpen;
  const menuId = `${generatedId}-menu`;
  const groups = columns ?? [{ ariaLabel: `${text} destinations`, items }];
  const hasDestinations = groups.some(group => group.items.length > 0);
  const flyout = useHoverFlyout({
    open,
    menuId,
    enabled: hasDestinations && !isMobile,
    onOpen: () => {
      if (coordinator) {
        coordinator.open(itemId);
      } else {
        setLocalOpen(true);
      }
    },
    onClose: () => {
      if (coordinator) {
        coordinator.close(itemId);
      } else {
        setLocalOpen(false);
      }
    },
  });

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (flyout.handlePanelKeyDown(event)) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      flyout.close(true);
      return;
    }

    const menuItems = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ),
    );
    const activeElement = document.activeElement;
    const currentIndex =
      activeElement instanceof HTMLElement
        ? menuItems.indexOf(activeElement)
        : -1;
    let nextIndex: number | undefined;

    if (event.key === 'ArrowDown') {
      nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
    } else if (event.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = menuItems.length - 1;
    }

    if (nextIndex !== undefined && menuItems[nextIndex]) {
      event.preventDefault();
      menuItems[nextIndex].focus();
    }
  };

  if (!hasDestinations || isMobile) {
    return <SidebarItem {...sidebarItemProps} text={text} to={to} />;
  }

  const content = (
    <div
      id={menuId}
      role="menu"
      tabIndex={-1}
      aria-label={`${text} navigation`}
      className={columns ? classes.columns : classes.menu}
      onKeyDown={handleMenuKeyDown}
    >
      {groups.map(group => (
        <div
          key={group.ariaLabel}
          role="group"
          aria-label={group.ariaLabel}
          className={columns ? classes.column : classes.singleColumn}
        >
          {group.items.map(item => (
            <SidebarFlyoutLink key={item.to} item={item} />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div
      ref={flyout.anchorRef}
      role="presentation"
      onMouseEnter={() => flyout.show()}
      onMouseLeave={flyout.handleMouseLeave}
      onBlur={flyout.handleBlur}
      onKeyDown={flyout.handleTriggerKeyDown}
    >
      <SidebarItem {...sidebarItemProps} text={text} to={to} />
      <SidebarHoverFlyoutPanel
        controller={flyout}
        placement="right-start"
        className={`${sharedClasses.paper} ${classes.paper}`}
      >
        {content}
      </SidebarHoverFlyoutPanel>
    </div>
  );
};
